import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AllocationRowState,
  CheckinRowState,
  VendorReceivingStep,
} from 'src/components/VendorReceiving/types';
import { setNightEnd } from 'src/state/slices/delivery';
import { VendorCheckinRows, VendorReceivingState, initialState } from './state';

export const vendorReceivingSlice = createSlice({
  name: 'vendorReceiving',
  initialState,
  reducers: {
    setCurrentStep: (
      state: VendorReceivingState,
      action: PayloadAction<VendorReceivingStep>,
    ) => {
      return {
        ...state,
        currentStep: action.payload,
      };
    },
    selectVendor: (
      state: VendorReceivingState,
      action: PayloadAction<{ vendorId: string; vendorName: string }>,
    ) => {
      return {
        ...state,
        selectedVendorId: action.payload.vendorId,
        selectedVendorName: action.payload.vendorName,
        currentStep: VendorReceivingStep.POCheckIn,
      };
    },
    clearSelectedVendor: (state: VendorReceivingState) => {
      return {
        ...state,
        selectedVendorId: null,
        selectedVendorName: null,
        currentStep: VendorReceivingStep.VendorList,
      };
    },
    setCheckinRow: (
      state: VendorReceivingState,
      action: PayloadAction<{
        vendorId: string;
        itemId: string;
        row: CheckinRowState;
      }>,
    ) => {
      const { vendorId, itemId, row } = action.payload;
      return {
        ...state,
        checkinRowsByVendorId: {
          ...state.checkinRowsByVendorId,
          [vendorId]: {
            ...(state.checkinRowsByVendorId[vendorId] ?? {}),
            [itemId]: row,
          },
        },
        hasEditsByVendorId: {
          ...state.hasEditsByVendorId,
          [vendorId]: true,
        },
      };
    },
    // Seeds Redux from the vendorReceivingCheckin server response. Existing
    // entries (the user's in-flight edits or an earlier seed) are preserved
    // so this can run safely on every query result.
    seedCheckinRowsFromServer: (
      state: VendorReceivingState,
      action: PayloadAction<{
        vendorId: string;
        items: Array<{ itemId: string; row: CheckinRowState }>;
      }>,
    ) => {
      const { vendorId, items } = action.payload;
      const existing = state.checkinRowsByVendorId[vendorId] ?? {};
      const next = { ...existing };
      items.forEach(({ itemId, row }) => {
        if (!(itemId in existing)) next[itemId] = row;
      });
      return {
        ...state,
        checkinRowsByVendorId: {
          ...state.checkinRowsByVendorId,
          [vendorId]: next,
        },
      };
    },
    // Hard replacement variant for view-mode entry — overwrites the
    // per-vendor map entirely so any user-typed values that diverge from
    // server are discarded. CustomerAllocation's receivedByItem calculation
    // reads from this map and would otherwise falsely flag over-allocation.
    // Also clears the hasEdits flag since we're now back in sync with server.
    replaceCheckinRowsFromServer: (
      state: VendorReceivingState,
      action: PayloadAction<{
        vendorId: string;
        items: Array<{ itemId: string; row: CheckinRowState }>;
      }>,
    ) => {
      const { vendorId, items } = action.payload;
      const next: VendorCheckinRows = {};
      items.forEach(({ itemId, row }) => {
        next[itemId] = row;
      });
      const nextHasEdits = { ...state.hasEditsByVendorId };
      delete nextHasEdits[vendorId];
      return {
        ...state,
        checkinRowsByVendorId: {
          ...state.checkinRowsByVendorId,
          [vendorId]: next,
        },
        hasEditsByVendorId: nextHasEdits,
      };
    },
    setAllocationRow: (
      state: VendorReceivingState,
      action: PayloadAction<{
        vendorId: string;
        orderLineId: string;
        row: AllocationRowState;
      }>,
    ) => {
      const { vendorId, orderLineId, row } = action.payload;
      return {
        ...state,
        allocationRowsByVendorId: {
          ...state.allocationRowsByVendorId,
          [vendorId]: {
            ...(state.allocationRowsByVendorId[vendorId] ?? {}),
            [orderLineId]: row,
          },
        },
        hasEditsByVendorId: {
          ...state.hasEditsByVendorId,
          [vendorId]: true,
        },
      };
    },
    // Drops all allocation rows for a vendor. Used when CustomerAllocation
    // enters view-mode mid-edit so the user's typed values don't leak into
    // resolvedRows / shortage / over-allocation calculations. The fallback
    // chain in the component (savedFulfillmentQuantity ?? orderedQty)
    // takes over once Redux is empty for that vendor. Also clears the
    // hasEdits flag since we've discarded those edits.
    clearAllocationRowsForVendor: (
      state: VendorReceivingState,
      action: PayloadAction<{ vendorId: string }>,
    ) => {
      const { vendorId } = action.payload;
      const nextAllocation = { ...state.allocationRowsByVendorId };
      delete nextAllocation[vendorId];
      const nextHasEdits = { ...state.hasEditsByVendorId };
      delete nextHasEdits[vendorId];
      return {
        ...state,
        allocationRowsByVendorId: nextAllocation,
        hasEditsByVendorId: nextHasEdits,
      };
    },
    setCheckinWindow: (
      state: VendorReceivingState,
      action: PayloadAction<{ vendorId: string; windowClosesAt: string }>,
    ) => {
      const { vendorId, windowClosesAt } = action.payload;
      // Auto-clear the server-forced override when the new cutoff is in
      // the future. Handles the "admin shortened → save rejected → admin
      // extended" sequence so the user isn't permanently locked once
      // server data confirms the window has been re-opened.
      const nextForceClosed = { ...state.forceClosedByVendorId };
      if (Date.now() < new Date(windowClosesAt).getTime()) {
        delete nextForceClosed[vendorId];
      }
      return {
        ...state,
        checkinWindowByVendorId: {
          ...state.checkinWindowByVendorId,
          [vendorId]: windowClosesAt,
        },
        forceClosedByVendorId: nextForceClosed,
      };
    },
    // Backend reported "Receiving window has closed" via a mutation
    // userError. Persisted in Redux (not local component state) so both
    // POCheckIn and CustomerAllocation derive view-mode from the same
    // signal. Auto-cleared when setCheckinWindow updates the timestamp
    // to a future value.
    setForceClosed: (
      state: VendorReceivingState,
      action: PayloadAction<{ vendorId: string }>,
    ) => {
      const { vendorId } = action.payload;
      return {
        ...state,
        forceClosedByVendorId: {
          ...state.forceClosedByVendorId,
          [vendorId]: true,
        },
      };
    },
    // Hard reset for "Discard changes" — wipes every per-vendor map and
    // returns the user to the vendor list. Used when the date picker is
    // locked and the user explicitly opts to throw away in-flight work.
    resetVendorReceivingState: () => initialState,
    clearVendorEntries: (
      state: VendorReceivingState,
      action: PayloadAction<{ vendorId: string }>,
    ) => {
      const { vendorId } = action.payload;
      const nextCheckinRows = { ...state.checkinRowsByVendorId };
      delete nextCheckinRows[vendorId];
      const nextAllocationRows = { ...state.allocationRowsByVendorId };
      delete nextAllocationRows[vendorId];
      const nextCheckinWindow = { ...state.checkinWindowByVendorId };
      delete nextCheckinWindow[vendorId];
      const nextHasEdits = { ...state.hasEditsByVendorId };
      delete nextHasEdits[vendorId];
      const nextForceClosed = { ...state.forceClosedByVendorId };
      delete nextForceClosed[vendorId];
      return {
        ...state,
        selectedVendorId: null,
        selectedVendorName: null,
        currentStep: VendorReceivingStep.VendorList,
        checkinRowsByVendorId: nextCheckinRows,
        allocationRowsByVendorId: nextAllocationRows,
        checkinWindowByVendorId: nextCheckinWindow,
        hasEditsByVendorId: nextHasEdits,
        forceClosedByVendorId: nextForceClosed,
      };
    },
  },
  // Vendor receiving state is per-(vendor, nightEnd). When the user changes
  // the viewing nightEnd anywhere in the app, all by-vendor maps refer to a
  // different day's data, so reset to initial state. URQL re-queries
  // automatically on the variable change and re-seeds via the seed effect.
  extraReducers: (builder) => {
    builder.addCase(setNightEnd.fulfilled, () => initialState);
  },
});
